import { styled } from '@linaria/react';
import { Redacted_Script } from 'next/font/google';

import { normalize } from '@/utils';
import useBrowser from '@/hooks/use-browser';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';
import Paper from '@/components/Paper';

const font = Redacted_Script({
  subsets: ['latin'],
  weight: ['400'],
  display: 'block',
});

interface Props {
  isFullscreened?: boolean;
}

function StickyTrickVisualization({ isFullscreened = false }: Props) {
  const browser = useBrowser();

  const content = (
    <Demo
      id="sticky-trick-visualization"
      initialValues={{
        rotateY: 0,
      }}
      controls={[
        <DemoSliderControl
          key="rotateY"
          id="rotateY"
          label="Reveal"
          min={0}
          max={100}
          step={1}
          previewRenderer={(val) => `${val}%`}
        />,
      ]}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
      outputWrapper={null}
    >
      {({ rotateY }) => (
        <Container
          data-browser={browser}
          style={{
            '--top-padding': `${normalize(rotateY, 0, 100, 0, 64)}px`,
            '--side-padding': `${normalize(
              rotateY,
              0,
              100,
              0,
              32
            )}px`,
          }}
        >
          <Browser
            style={{
              '--rotate-y': `${normalize(
                rotateY,
                0,
                100,
                0,
                -45
              )}deg`,
              '--translate-x': `${normalize(
                rotateY,
                0,
                100,
                0,
                0
              )}px`,
              '--skew-y': `${normalize(rotateY, 0, 100, 0, 10)}deg`,
            }}
          >
            <Content
              style={{
                '--gap': `${normalize(rotateY, 0, 100, 0, 16)}px`,
              }}
            >
              <Hero
                style={{
                  '--lightness': `${normalize(
                    rotateY,
                    0,
                    100,
                    88,
                    80
                  )}%`,
                  '--saturation': `${normalize(
                    rotateY,
                    0,
                    100,
                    90,
                    60
                  )}%`,
                }}
              >
                <HeroBlocker
                  style={{
                    '--translate-z': `${normalize(
                      rotateY,
                      0,
                      100,
                      0,
                      20
                    )}px`,
                  }}
                />
                <p className={font.className} aria-hidden="true">
                  Words that have been obscured and covered.
                </p>
              </Hero>
              <Body
                style={{
                  '--body-opacity': `${normalize(
                    rotateY,
                    0,
                    100,
                    1,
                    0.85
                  )}`,
                }}
              >
                <BodyBlocker
                  style={{
                    '--translate-z': `${normalize(
                      rotateY,
                      0,
                      100,
                      0,
                      20
                    )}px`,
                  }}
                />
                <p className={font.className} aria-hidden="true">
                  These words are meant to mimic a blog post. I need
                  quite a few of them, possibly across multiple
                  paragraphs!
                </p>
                <p className={font.className} aria-hidden="true">
                  Indeed, it's a lot of words.
                </p>
                <p className={font.className} aria-hidden="true">
                  If you have figured out how to read this, you're a
                  real clever duck! Congrats {'<3'}.
                </p>
              </Body>
              <StickyHeaderWrapper>
                <StickyHeader
                  style={{
                    '--outline-opacity': `${normalize(
                      rotateY,
                      0,
                      100,
                      0,
                      0.88
                    )}`,
                    '--translate-z': `${normalize(
                      rotateY,
                      0,
                      100,
                      0,
                      24
                    )}px`,
                  }}
                >
                  <Name>Josh Comeau</Name>
                  <Links>
                    <Link>Categories</Link>
                    <Link>Courses</Link>
                    <Link>About</Link>
                  </Links>
                </StickyHeader>
              </StickyHeaderWrapper>
            </Content>
          </Browser>
        </Container>
      )}
    </Demo>
  );
  if (isFullscreened) {
    return <FullscreenWrapper>{content}</FullscreenWrapper>;
  } else {
    return <InlineWrapper>{content}</InlineWrapper>;
  }
}

const FullscreenWrapper = styled.div`
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-content: center;
  background: var(--color-background);
`;

const InlineWrapper = styled.div``;

const Container = styled(Space)`
  position: relative;
  height: 400px;
  width: 600px;
  max-width: 100%;
  overflow: auto;
  border: 1px solid var(--color-gray-200);
  border-radius: 4px;
  padding: var(--top-padding) var(--side-padding) 0;
  perspective: 800px;
  transform-style: preserve-3d;
  overflow-x: hidden;
  margin-inline: auto;

  &[data-browser='firefox'] {
    perspective: none;
    transform-style: none;
  }
`;

const Browser = styled.div`
  position: relative;
  transform: rotateY(var(--rotate-y)) translateX(var(--translate-x));
  perspective: inherit;
  transform-style: inherit;

  ${Container}[data-browser='firefox'] & {
    transform: skewY(var(--skew-y));
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  perspective: inherit;
  transform-style: inherit;
`;

const Hero = styled.div`
  height: 200px;
  background: hsl(200deg var(--saturation) var(--lightness));
  perspective: inherit;
  transform-style: inherit;

  p {
    font-size: 1.5rem;
    color: black;
    padding: 32px;
    text-wrap: balance;
    max-width: 500px;
  }
`;

const Body = styled.div`
  height: 500px;
  perspective: inherit;
  transform-style: inherit;
  background: hsl(0deg 0% 100% / var(--body-opacity));

  p {
    font-size: 1rem;
    color: black;
    padding-inline: 32px;
    max-width: 400px;
    margin-bottom: 1rem;
  }
`;

const Blocker = styled.div`
  position: sticky;
  top: 0;
  height: 50px;
  width: 100%;
`;

const HeroBlocker = styled(Blocker)`
  background: hsl(200deg 90% 88%);
  transform: translateZ(var(--translate-z));
`;

const BodyBlocker = styled(Blocker)`
  background: hsl(0deg 0% 100%);
  transform: translateZ(var(--translate-z));
`;

const StickyHeaderWrapper = styled.div`
  position: absolute;
  inset: 0;
  perspective: inherit;
  transform-style: inherit;
`;

const StickyHeader = styled(Paper)`
  position: sticky;
  top: 0;
  height: 50px;
  width: 100%;
  display: flex;
  align-items: baseline;
  white-space: nowrap;
  gap: 3rem;
  padding-inline: 32px;
  padding-top: 0.75rem;
  background: hsl(0deg 0% 100% / 0);
  /* border: 1px solid var(--color-gray-900); */
  transform: translateZ(var(--translate-z));
  border-radius: 0;
  outline: 3px dashed hsl(210deg 90% 95% / var(--outline-opacity));
`;

const Name = styled.div`
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-primary);
`;

const Links = styled.ul`
  display: flex;
  gap: 2rem;
  list-style: none;

  @media (max-width: 41rem) {
    display: none;
  }
`;
const Link = styled.li`
  font-size: 0.75rem;
`;

export default StickyTrickVisualization;
